/** Auth0 callback / PKCE handshake failures that must not open-endedly re-login. */
const AUTH_CALLBACK_FAILURE_CODES = new Set(["missing_transaction", "state_mismatch"]);

export const isAuthCallbackFailure = (err: unknown): boolean => {
  if (!err || typeof err !== "object") return false;
  const code = (err as { error?: unknown }).error;
  return typeof code === "string" && AUTH_CALLBACK_FAILURE_CODES.has(code);
};

export const getAuthErrorCode = (err: unknown): string | undefined => {
  if (!err || typeof err !== "object") return undefined;
  const code = (err as { error?: unknown }).error;
  return typeof code === "string" ? code : undefined;
};

/**
 * Decision table for a failed Auth0 redirect callback.
 *
 * - retry: first failure → strip dead ?code= and call loginWithRedirect once
 * - fatal: already retried (or persisted error) → show error UI, do not redirect again
 * - wait: Auth0 still initializing
 * - pass: no callback failure → render children / normal auth gate
 */
export type AuthCallbackFailureAction = "wait" | "pass" | "retry" | "fatal";

export function resolveAuthCallbackFailureAction(input: {
  isLoading: boolean;
  error: unknown;
  alreadyRetried: boolean;
  hasPersistedAuthError: boolean;
}): AuthCallbackFailureAction {
  if (input.hasPersistedAuthError) return "fatal";
  if (input.isLoading) return "wait";
  if (!isAuthCallbackFailure(input.error)) return "pass";
  return input.alreadyRetried ? "fatal" : "retry";
}

/** Remove OAuth callback params so a dead ?code= cannot be re-processed. */
export function stripAuthCallbackSearchParams(search: string): { search: string; didStrip: boolean } {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const keys = ["code", "state", "error", "error_description", "session_state"];
  let didStrip = false;
  for (const key of keys) {
    if (params.has(key)) {
      params.delete(key);
      didStrip = true;
    }
  }
  const next = params.toString();
  return { search: next ? `?${next}` : "", didStrip };
}
